import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
    ArrowUpRightIcon,
    Button,
    ButtonSize,
    ButtonVariant,
    CreditCardIcon,
    DownloadIcon,
    FruitfulCustomIcon,
    FullScreenSpinner,
    H1Emphasized,
    Icon,
    InfoButton,
    MailIcon,
    MessageCircleQuestionMarkIcon,
    NavigationRow,
    NotebookTextIcon,
    Private,
    Row,
    RowList,
    ScreenContainer,
    TileButton,
    UploadIcon,
    UsersRoundIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useSendEmailToSupport } from '../../../common/hooks/useSendEmailToSupport';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import {
    canRequestCard,
    getDefaultedAccountNickname,
} from '../../../common/utils/bankingUtils';
import {
    accountHasNonTerminatedCardsFromOtherHolder,
    getNonTerminatedCardsForUser,
    invertNormalizedValue,
} from '../../../common/utils/cardUtils';
import { IS_PRODUCTION } from '../../../config/FruitfulEnvironment';
import { captureMessage, trackView } from '../../../logger/Logger';
import useGetIncomeSplitFromGlobalState from '../../hooks/income-split/useGetIncomeSplitFromGlobalState';
import useGetIncomeSplitFromGlobalStateNew from '../../hooks/income-split/useGetIncomeSplitFromGlobalStateNew';
import useShouldSeeIncomeSplit from '../../hooks/income-split/useShouldSeeIncomeSplit';
import useShouldSeeTiedRules from '../../hooks/income-split/useShouldSeeRuleSets';
import useGetAccountDisplayDetailsFromGlobalState from '../../hooks/useGetAccountDisplayDetailsFromGlobalState';
import {
    detectedPaychecksAtom,
    existingPaycheckRoutingConfigsAtom,
} from '../../state/PaycheckRoutingAtoms';
import {
    preselectedAccountForCardAtom,
    selectedAccountForDirectDepositAtom,
} from '../../state/SpacesAtoms';
import { preselectedTransferAccountsAtom } from '../../state/TransferAtoms';
import {
    AccountType,
    KYCVerificationStatus,
    RelationshipStatus,
} from '../../types/BankAccount';
import type { PaycheckRoutingIncomeDetail } from '../../types/PaycheckRouting';
import CardStack from '../cards/CardStack';
import { IncomeSplitCard } from '../cards/IncomeSplitCard';
import { PaycheckSplit } from '../cards/PaycheckSplit';
import { BankDisclosureCashCardFootnote } from '../Disclosures';
import InterestCashBackEarned from './InterestCashBackEarned';
import AvailableBalanceInfoSheet from './sheets/AvailableBalanceInfoSheet';
import TransactionTable from './transactions/transactionTable/TransactionTable';

const JointUserIcon = () => {
    return (
        <View
            backgroundColor="$gray100"
            padding={8}
            borderRadius={100}
            position="absolute"
            marginLeft={55}
            marginTop={-10}
        >
            <Icon icon={UsersRoundIcon} size={16} color="$gray1000" />
        </View>
    );
};

const SpaceDetails = () => {
    const user = useAuthedUser();
    const sendEmailToSupport = useSendEmailToSupport();

    if (user.syncteraKycStatus !== KYCVerificationStatus.ACCEPTED) {
        return <Redirect href="/home/banking" />;
    }

    const { syncteraAccountId, forceRefetch } = useLocalSearchParams<{
        syncteraAccountId: string;
        forceRefetch?: string;
    }>();

    useEffect(() => {
        trackView('Banking/SpacesOverview/SpaceDetails');
    }, [syncteraAccountId]);

    const setPreselectedTransferAccounts = useSetAtom(
        preselectedTransferAccountsAtom,
    );
    const setPreselectedAccountForCardAtom = useSetAtom(
        preselectedAccountForCardAtom,
    );

    const setAccountForDirectDeposit = useSetAtom(
        selectedAccountForDirectDepositAtom,
    );

    const [
        isAvailableBalanceInfoSheetOpen,
        setIsAvailableBalanceInfoSheetOpen,
    ] = useState(false);

    const { account, isLoading } = useGetAccountDisplayDetailsFromGlobalState({
        syncteraAccountId,
    });

    const existingPaycheckRoutingConfigs = useAtomValue(
        existingPaycheckRoutingConfigsAtom,
    );

    const paychecks: PaycheckRoutingIncomeDetail[] = useAtomValue(
        detectedPaychecksAtom,
    );

    const paycheckRoutingConfigs = (
        existingPaycheckRoutingConfigs ?? []
    ).filter(
        (paycheckRoutingConfig) =>
            paycheckRoutingConfig?.accountId === account?.bankAccountId,
    );

    const paychecksForAccount = (paychecks ?? []).filter(
        (paycheck) => paycheck?.accountId === account?.bankAccountId,
    );

    const shouldSeeIncomeSplit = useShouldSeeIncomeSplit();
    const shouldSeeTiedRules = useShouldSeeTiedRules();

    const { incomeSplit: incomeSplitLegacy } =
        useGetIncomeSplitFromGlobalState();
    const { incomeSplit } = useGetIncomeSplitFromGlobalStateNew();

    const splitConfig = useMemo(() => {
        // If shouldSeeTiedRules is true, only use incomeSplit (ignore legacy)
        // If shouldSeeTiedRules is false, use legacy
        return shouldSeeTiedRules ? incomeSplit : incomeSplitLegacy;
    }, [shouldSeeTiedRules, incomeSplit, incomeSplitLegacy]);

    const incomeSplitRuleAccountIds = useMemo(() => {
        return splitConfig?.accounts?.map((item) => item?.accountId) ?? [];
    }, [splitConfig]);

    const incomeSplitWatchesAccount = useMemo(() => {
        return (
            !!splitConfig &&
            incomeSplitRuleAccountIds.includes(account?.bankAccountId ?? '')
        );
    }, [splitConfig, incomeSplitRuleAccountIds, account?.bankAccountId]);

    const reportAnIssue = () => {
        void sendEmailToSupport({ subject: 'Report an issue' });
    };

    if (syncteraAccountId == null) {
        captureMessage('Space not found for details page');
        return <Redirect href="/home/banking" />;
    }

    if (account == null && isLoading) {
        return <FullScreenSpinner />;
    }

    if (account == null) {
        captureMessage(
            `Could not load account ${syncteraAccountId} for member ${user.id}. This may happen if a joint user has not accepted their invitation`,
        );
        return <Redirect href="/home/banking" />;
    }

    const {
        bankAccountId,
        accountType,
        availableToWithdraw,
        currentBalance,
        cardBalance,
        interestEarned,
        cashBackEarned,
        pendingTransfers,
        pendingCardTransactions,
        icon,
        color,
        jointUserRelationshipStatus,
        chargeSecuredSyncteraAccountId,
        hasCard: accountHasCard,
        accountNumber,
    } = account;

    const cardBalanceNormalized = invertNormalizedValue(cardBalance);
    const canIssueCard = canRequestCard(user.syncteraPersonId, account);
    const hasCardForUser =
        getNonTerminatedCardsForUser(user.syncteraPersonId, account.cards)
            .length > 0;

    const showPaycheckSplit =
        (paychecksForAccount.length > 0 || paycheckRoutingConfigs.length > 0) &&
        paycheckRoutingConfigs.some(
            (paycheckRoutingConfig) => paycheckRoutingConfig.isLegacy,
        );

    return (
        <ScreenContainer
            title={getDefaultedAccountNickname(account)}
            hasBackButton={true}
        >
            <XStack alignSelf="center" marginTop={10}>
                {/* Additional margin to prevent absolute positioned joint user icon cutoff */}
                <FruitfulCustomIcon icon={icon} color={color} size="lg" />
                {jointUserRelationshipStatus === RelationshipStatus.ACTIVE && (
                    <JointUserIcon />
                )}
            </XStack>
            <Private>
                <H1Emphasized alignSelf="center" marginTop={14}>
                    {availableToWithdraw}
                </H1Emphasized>
            </Private>
            <XStack marginBottom={10} justifyContent="center">
                <InfoButton
                    subtle
                    text="Available"
                    onPress={() => {
                        setIsAvailableBalanceInfoSheetOpen(true);
                    }}
                />
            </XStack>
            <InterestCashBackEarned
                interestEarned={interestEarned}
                cashBackEarned={cashBackEarned}
                showCashBack={false}
            />
            <YStack gap={16}>
                <XStack justifyContent="space-between" gap={8} height={54}>
                    <TileButton
                        icon={DownloadIcon}
                        text="Transfer in"
                        onPress={() => {
                            setPreselectedTransferAccounts({
                                toSyncteraAccountId: account?.syncteraAccountId,
                            });
                            router.navigate('/banking/transfer');
                        }}
                    />
                    <TileButton
                        icon={UploadIcon}
                        text="Transfer out"
                        onPress={() => {
                            setPreselectedTransferAccounts({
                                fromSyncteraAccountId:
                                    account?.syncteraAccountId,
                            });
                            router.navigate('/banking/transfer');
                        }}
                    />
                    <TileButton
                        icon={NotebookTextIcon}
                        text="View account"
                        onPress={() => {
                            router.navigate({
                                pathname: '/banking/account-details',
                                params: { syncteraAccountId },
                            });
                        }}
                    />
                </XStack>
                {hasCardForUser && chargeSecuredSyncteraAccountId != null && (
                    <RowList>
                        <NavigationRow
                            onPress={() => {
                                router.navigate({
                                    pathname: '/banking/card/manage-card',
                                    params: {
                                        syncteraAccountId:
                                            account.syncteraAccountId,
                                    },
                                });
                            }}
                            leftContentOverride={
                                <CardStack account={account} />
                            }
                            title={`${account.nickname} Card`}
                        />
                    </RowList>
                )}
                {showPaycheckSplit ? (
                    <PaycheckSplit
                        paycheckRoutingConfigs={paycheckRoutingConfigs}
                        paychecks={paychecksForAccount}
                        accountId={account.bankAccountId}
                    />
                ) : null}
                {shouldSeeIncomeSplit && incomeSplitWatchesAccount ? (
                    <IncomeSplitCard status={splitConfig?.status} />
                ) : null}
                <TransactionTable
                    bankAccountId={bankAccountId}
                    syncteraAccountId={syncteraAccountId}
                    forceRefetch={forceRefetch === 'true'}
                    allowPagination={false}
                    isSingleTableView={true}
                />
                <Button
                    text="View all transactions"
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.SMALL}
                    onPress={() => {
                        router.navigate({
                            pathname: '/banking/transactions',
                            params: { bankAccountId, syncteraAccountId },
                        });
                    }}
                    buttonProps={{
                        width: 'auto',
                    }}
                />

                <RowList>
                    {canIssueCard && (
                        <NavigationRow
                            title="Add a Fruitful Card"
                            leftIcon={CreditCardIcon}
                            onPress={() => {
                                setPreselectedAccountForCardAtom(account);
                                router.navigate({
                                    pathname: '/banking/onboarding/add-card',
                                    // If the other account holder as already issued cards, force this user's choices to be the same
                                    ...(accountHasNonTerminatedCardsFromOtherHolder(
                                        user.syncteraPersonId,
                                        account,
                                    )
                                        ? {
                                              params: {
                                                  isJointInvitation: 'true',
                                              },
                                          }
                                        : {}),
                                });
                            }}
                        />
                    )}
                    {hasCardForUser && (
                        <NavigationRow
                            title="Manage card"
                            subtitle="Payments • Card details"
                            leftIcon={CreditCardIcon}
                            href={{
                                pathname: '/banking/card/manage-card',
                                params: { syncteraAccountId },
                            }}
                        />
                    )}
                    <NavigationRow
                        title="Direct deposit"
                        subtitle="Send my direct deposit to this account"
                        leftIcon={DownloadIcon}
                        onPress={() => {
                            setAccountForDirectDeposit(account);
                            router.push({
                                pathname: '/banking/direct-deposit/info',
                                params: {
                                    context: 'banking',
                                    source: 'banking-details',
                                },
                            });
                        }}
                    />
                    {accountType === AccountType.CHECKING && (
                        <NavigationRow
                            title="Fruitful Cash FAQ"
                            subtitle="More about Fruitful Cash"
                            leftIcon={MessageCircleQuestionMarkIcon}
                            href="/settings/faq"
                        />
                    )}
                    <Row
                        title="Report a problem"
                        subtitle="Send us an email to report"
                        leftIcon={MailIcon}
                        rightContent={<Icon icon={ArrowUpRightIcon} />}
                        onPress={reportAnIssue}
                    />
                    {!IS_PRODUCTION && (
                        <NavigationRow
                            title="Simulate incoming ACH"
                            href={{
                                pathname: '/banking/simulate-incoming-ach',
                                params: {
                                    accountNumber,
                                },
                            }}
                        />
                    )}
                </RowList>
                <View>
                    <BankDisclosureCashCardFootnote />
                </View>
            </YStack>
            <AvailableBalanceInfoSheet
                totalCurrentBalance={currentBalance}
                totalAvailableBalance={availableToWithdraw}
                totalCardBalance={cardBalanceNormalized}
                totalPendingTransfers={pendingTransfers}
                totalPendingCardTransactions={pendingCardTransactions}
                isOpen={isAvailableBalanceInfoSheetOpen}
                onClose={() => {
                    setIsAvailableBalanceInfoSheetOpen(false);
                }}
                hasCards={accountHasCard}
            />
        </ScreenContainer>
    );
};

export default SpaceDetails;
